import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { createCampaign, getEmailAccounts } from '../services/api';

function CreateCampaign() {
  const location = useLocation();
  const navigate = useNavigate();
  const isFromTaskRecord = new URLSearchParams(location.search).get('fromTask') === 'true' || 
                           document.referrer.includes('/tasks/');
  
  const [formData, setFormData] = useState({
    campaignName: '',
    subjectLine: '',
    htmlContent: ''
  });
  const [recipientEmails, setRecipientEmails] = useState('');
  const [csvFile, setCsvFile] = useState(null);
  const [useCsv, setUseCsv] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [accounts, setAccounts] = useState([]);
  const [responseButtons, setResponseButtons] = useState([
    { label: 'Yes', value: 'yes', color: '#2563eb' },
    { label: 'No', value: 'no', color: '#6b7280' }
  ]);

  useEffect(() => {
    loadAccounts();
  }, []);

  const loadAccounts = async () => {
    try {
      const response = await getEmailAccounts();
      if (response.success) {
        setAccounts(response.accounts || []);
      }
    } catch (err) {
      console.error('Error loading accounts:', err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      let recipientList = null;
      if (!isFromTaskRecord) {
        recipientList = useCsv
          ? null
          : recipientEmails
              .split(/[\n,;]+/)
              .map(email => email.trim())
              .filter(email => email && email.includes('@'));
        
        if (!useCsv && (!recipientList || recipientList.length === 0)) {
          setError('Please enter at least one recipient email or upload a CSV file');
          setLoading(false);
          return;
        }
      } else {
        if (recipientEmails.trim()) {
          recipientList = recipientEmails
            .split(/[\n,;]+/)
            .map(email => email.trim())
            .filter(email => email && email.includes('@'));
        }
      }

      const isTemplate = isFromTaskRecord;
      
      const campaignData = {
        campaignName: formData.campaignName,
        subjectLine: formData.subjectLine,
        htmlContent: formData.htmlContent,
        recipientEmails: recipientList || [],
        responseConfig: responseButtons,
        isTemplate: isTemplate
      };

      const response = await createCampaign(campaignData, csvFile);
      if (response.success) {
        navigate(`/campaigns/${response.campaignId}`);
      } else {
        setError(response.message || 'Failed to create campaign');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create campaign');
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.type === 'text/csv' || file.name.endsWith('.csv')) {
        setCsvFile(file);
        setError('');
      } else {
        setError('Please upload a CSV file');
        setCsvFile(null);
      }
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0e27] py-8 px-4 relative z-10">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-semibold text-white mb-2">Create Campaign</h1>
          <p className="text-sm text-gray-400">Set up a new email campaign</p>
        </div>

        {/* Warning Messages */}
        {accounts.length === 0 && (
          <div className="mb-6 p-4 bg-yellow-900/20 border border-yellow-500/30 rounded-lg backdrop-blur-sm">
            <p className="text-yellow-400 text-sm font-medium mb-2">⚠️ No Email Accounts Connected</p>
            <p className="text-yellow-300 text-xs mb-2">You need to connect at least one Gmail account before creating a campaign.</p>
            <a href="/email-accounts" className="text-blue-400 hover:text-blue-300 text-sm font-medium">
              Connect Gmail Account →
            </a>
          </div>
        )}

        {error && (
          <div className="mb-6 p-4 bg-red-900/20 border border-red-500/30 rounded-lg backdrop-blur-sm">
            <p className="text-red-400 text-sm">{error}</p>
          </div>
        )}

        {/* Main Form */}
        <form onSubmit={handleSubmit} className="bg-[#1a1f3a]/80 border border-blue-500/20 rounded-lg p-8 space-y-6 backdrop-blur-sm">
          {/* Campaign Name */}
          <div>
            <label htmlFor="campaignName" className="block text-sm font-medium text-gray-300 mb-2">
              Campaign Name <span className="text-red-400">*</span>
            </label>
            <input
              id="campaignName"
              type="text"
              value={formData.campaignName}
              onChange={(e) => setFormData({ ...formData, campaignName: e.target.value })}
              required
              className="w-full px-4 py-3 bg-black/50 border border-gray-700 rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none focus:border-blue-500 transition-all"
              placeholder="Summer Sale Campaign"
            />
          </div>

          {/* Subject Line */}
          <div>
            <label htmlFor="subjectLine" className="block text-sm font-medium text-gray-300 mb-2">
              Subject Line <span className="text-red-400">*</span>
            </label>
            <input
              id="subjectLine"
              type="text"
              value={formData.subjectLine}
              onChange={(e) => setFormData({ ...formData, subjectLine: e.target.value })}
              required
              className="w-full px-4 py-3 bg-black/50 border border-gray-700 rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none focus:border-blue-500 transition-all"
              placeholder="Check out our amazing deals!"
            />
          </div>
          
          {/* Email Content */}
          <div>
            <label htmlFor="htmlContent" className="block text-sm font-medium text-gray-300 mb-2">
              Email Content (HTML) <span className="text-red-400">*</span>
            </label>
            <textarea
              id="htmlContent"
              value={formData.htmlContent}
              onChange={(e) => setFormData({ ...formData, htmlContent: e.target.value })}
              required
              rows={12}
              className="w-full px-4 py-3 bg-black/50 border border-gray-700 rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none focus:border-blue-500 font-mono resize-none transition-all"
              placeholder="<h1>Hello!</h1><p>This is your email content...</p>"
            />
          </div>

          {/* Recipients Section */}
          <div className="border-t border-gray-700 pt-6">
            <div className="flex items-center mb-4">
              <input
                type="checkbox"
                id="useCsv"
                checked={useCsv}
                onChange={(e) => {
                  setUseCsv(e.target.checked);
                  if (e.target.checked) {
                    setRecipientEmails('');
                  }
                }}
                className="w-4 h-4 text-blue-600 bg-black border-gray-600 rounded focus:ring-blue-500"
              />
              <label htmlFor="useCsv" className="ml-3 block text-sm font-medium text-gray-300 cursor-pointer">
                Upload CSV file instead of manual entry
              </label>
            </div>

            {useCsv ? (
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Upload CSV File <span className="text-red-400">*</span>
                </label>
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleFileChange}
                  required={useCsv}
                  className="block w-full text-sm text-gray-400 file:mr-4 file:py-2 file:px-4 file:border file:border-gray-700 file:bg-black/50 file:text-white file:rounded-lg file:cursor-pointer hover:file:bg-gray-800 transition-all"
                />
                <p className="mt-2 text-xs text-gray-400">
                  CSV format: First column should contain email addresses. One email per row.
                </p>
              </div>
            ) : (
              <div>
                <label htmlFor="recipientEmails" className="block text-sm font-medium text-gray-300 mb-2">
                  Recipient Emails (one per line) {isFromTaskRecord ? '(Optional - email comes from task record)' : '*'}
                </label>
                {isFromTaskRecord && (
                  <div className="mb-4 p-4 bg-blue-900/20 border border-blue-500/30 rounded-lg">
                    <p className="text-sm font-medium text-blue-400 mb-1">✓ Sending from Task Record</p>
                    <p className="text-xs text-gray-400 mb-1">
                      You don't need to enter recipient emails here. When you send email from a task record, 
                      the recipient email will be automatically taken from that record.
                    </p>
                    <p className="text-xs text-gray-500 italic">
                      This campaign will be used as a template (subject + content) for sending to record emails.
                    </p>
                  </div>
                )}
                <textarea
                  id="recipientEmails"
                  value={recipientEmails}
                  onChange={(e) => setRecipientEmails(e.target.value)}
                  required={!useCsv && !isFromTaskRecord}
                  rows={8}
                  className="w-full px-4 py-3 bg-black/50 border border-gray-700 rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none focus:border-blue-500 font-mono resize-none transition-all"
                  placeholder={isFromTaskRecord 
                    ? "Optional: Enter emails if you also want to send this campaign to other recipients\nemail1@example.com\nemail2@example.com"
                    : "email1@example.com\nemail2@example.com\nemail3@example.com"}
                />
              </div>
            )}
          </div>

          {/* Response Buttons Section */}
          <div className="border-t border-gray-700 pt-6">
            <label className="block text-sm font-medium text-gray-300 mb-3">
              Response Buttons (Template Design)
            </label>
            <p className="text-xs text-gray-400 mb-4">
              Customize the response buttons that will appear in your emails. Recipients can click these to record their response.
            </p>
            
            <div className="space-y-3">
              {responseButtons.map((button, index) => (
                <div key={index} className="flex items-center gap-3 p-4 bg-black/50 border border-gray-700 rounded-lg">
                  <div className="flex-1 grid grid-cols-3 gap-3">
                    <input
                      type="text"
                      value={button.label}
                      onChange={(e) => {
                        const newButtons = [...responseButtons];
                        newButtons[index].label = e.target.value;
                        setResponseButtons(newButtons);
                      }}
                      placeholder="Button Label"
                      className="px-3 py-2 bg-black/50 border border-gray-700 rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none focus:border-blue-500"
                    />
                    <input
                      type="text"
                      value={button.value}
                      onChange={(e) => {
                        const newButtons = [...responseButtons];
                        newButtons[index].value = e.target.value;
                        setResponseButtons(newButtons);
                      }}
                      placeholder="Response Value"
                      className="px-3 py-2 bg-black/50 border border-gray-700 rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none focus:border-blue-500"
                    />
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={button.color}
                        onChange={(e) => {
                          const newButtons = [...responseButtons];
                          newButtons[index].color = e.target.value;
                          setResponseButtons(newButtons);
                        }}
                        className="h-10 w-16 border border-gray-700 rounded cursor-pointer bg-black/50"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          setResponseButtons(responseButtons.filter((_, i) => i !== index));
                        }}
                        className="px-3 py-2 text-xs font-medium text-red-400 hover:text-red-300 border border-red-500/30 hover:border-red-500/50 rounded-lg transition-all"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                </div>
              ))}
              
              <button
                type="button"
                onClick={() => {
                  setResponseButtons([...responseButtons, { label: '', value: '', color: '#2563eb' }]);
                }}
                className="w-full px-4 py-3 border-2 border-dashed border-gray-700 bg-black/30 text-gray-300 hover:text-white hover:border-blue-500 rounded-lg transition-all text-sm font-medium"
              >
                + Add Response Button
              </button>
            </div>

            {/* Preview */}
            <div className="mt-4 p-4 bg-black/50 border border-gray-700 rounded-lg">
              <p className="text-xs font-medium text-gray-300 mb-3">Preview:</p>
              <div className="flex flex-wrap gap-2">
                {responseButtons.map((button, index) => (
                  <button
                    key={index}
                    type="button"
                    disabled
                    style={{ 
                      backgroundColor: button.color,
                      color: button.color === '#000000' || button.color === '#2563eb' ? '#ffffff' : '#000000',
                    }}
                    className="px-4 py-2 text-sm font-medium rounded-lg cursor-default border border-gray-700"
                  >
                    {button.label || 'Button Label'}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end gap-3 pt-6 border-t border-gray-700">
            <button
              type="button"
              onClick={() => navigate('/campaigns')}
              className="px-6 py-3 text-sm font-medium text-gray-300 bg-gray-800 hover:bg-gray-700 rounded-lg transition-all"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || accounts.length === 0}
              className="px-6 py-3 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-all shadow-lg shadow-blue-600/30 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Creating...' : 'Create Campaign'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default CreateCampaign;
